// Rego/COF renewal-date "spacing" -- spreads renewal costs evenly across
// the months an admin is actually willing to buy in (e.g. never
// December/January), instead of them clustering wherever the fleet
// happens to have landed historically. Mirrors the by-hand spreadsheet
// Janelle used to run: count how many vehicles fall in each upcoming
// month, then when renewing a given vehicle, steer it toward whichever
// allowed month is currently lightest.
//
// Rego and COF are handled differently because they behave differently:
// - Rego can be bought for any number of months, so the "lever" is simply
//   choosing a renewal length that lands the new expiry in a light month.
// - COF/WOF renewal length is fixed by regulation (this vehicle's own
//   cof_interval_months, or the fleet default -- not every vehicle is on
//   the same schedule, e.g. a WOF vehicle on 12 months vs COF on 6). The
//   only real lever is booking the inspection up to fleet_cof_flex_months
//   EARLY, since in NZ a check done early keeps the next expiry counted
//   from the original due date, not the check date. Booking it late isn't
//   a real option -- that's just non-compliance -- so the flex window only
//   ever looks backward from the baseline anniversary, never forward.
import { supabase } from "@/lib/supabase";

export const SETTING_REGO_ALLOWED_MONTHS = "fleet_rego_allowed_months";
export const SETTING_COF_ALLOWED_MONTHS = "fleet_cof_allowed_months";
export const SETTING_COF_DEFAULT_INTERVAL_MONTHS = "fleet_cof_default_interval_months";
export const SETTING_COF_FLEX_MONTHS = "fleet_cof_flex_months";

// Default excludes December/January, per Janelle's own spacing rule --
// admins elsewhere may want a different set, hence this being a setting.
export const DEFAULT_REGO_ALLOWED_MONTHS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
export const DEFAULT_COF_ALLOWED_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
export const DEFAULT_COF_INTERVAL_MONTHS = 6;
export const DEFAULT_COF_FLEX_MONTHS = 1;

export interface FleetSpacingSettings {
  regoAllowedMonths: number[];
  cofAllowedMonths: number[];
  cofDefaultIntervalMonths: number;
  cofFlexMonths: number;
}

export function parseMonthsCsv(csv: string | null | undefined, fallback: number[]): number[] {
  if (!csv) return fallback;
  const months = csv
    .split(",")
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n) && n >= 1 && n <= 12);
  return months.length > 0 ? [...new Set(months)].sort((a, b) => a - b) : fallback;
}

export function monthsToCsv(months: number[]): string {
  return [...new Set(months)].sort((a, b) => a - b).join(",");
}

export async function loadFleetSpacingSettings(): Promise<FleetSpacingSettings> {
  const { data } = await supabase
    .from("settings")
    .select("name, value")
    .in("name", [
      SETTING_REGO_ALLOWED_MONTHS,
      SETTING_COF_ALLOWED_MONTHS,
      SETTING_COF_DEFAULT_INTERVAL_MONTHS,
      SETTING_COF_FLEX_MONTHS,
    ]);

  const byName = new Map((data || []).map((s) => [s.name, s.value as string | null]));
  const intervalRaw = byName.get(SETTING_COF_DEFAULT_INTERVAL_MONTHS);
  const flexRaw = byName.get(SETTING_COF_FLEX_MONTHS);
  const interval = intervalRaw ? parseInt(intervalRaw, 10) : NaN;
  const flex = flexRaw ? parseInt(flexRaw, 10) : NaN;

  return {
    regoAllowedMonths: parseMonthsCsv(byName.get(SETTING_REGO_ALLOWED_MONTHS), DEFAULT_REGO_ALLOWED_MONTHS),
    cofAllowedMonths: parseMonthsCsv(byName.get(SETTING_COF_ALLOWED_MONTHS), DEFAULT_COF_ALLOWED_MONTHS),
    cofDefaultIntervalMonths: Number.isFinite(interval) && interval > 0 ? interval : DEFAULT_COF_INTERVAL_MONTHS,
    cofFlexMonths: Number.isFinite(flex) && flex >= 0 ? flex : DEFAULT_COF_FLEX_MONTHS,
  };
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function monthLabel(monthIndex0: number): string {
  return MONTH_LABELS[((monthIndex0 % 12) + 12) % 12];
}

function addMonths(date: Date, months: number): Date {
  const d = new Date(date.getTime());
  d.setUTCMonth(d.getUTCMonth() + months);
  return d;
}

// yyyy-mm-dd -> UTC midnight Date, avoiding local-timezone month drift
// (new Date("2026-02-01") is safe, but arithmetic on it via setMonth needs
// to stay in UTC to avoid NZT ever rolling a date into the wrong month).
function parseDateOnly(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
}

export interface MonthBucket {
  year: number;
  monthIndex0: number;
  label: string;
  count: number;
  allowed: boolean;
}

// Forward-looking month-by-month count of how many active vehicles
// currently have their rego/COF date landing in each month -- the same
// table Janelle's spreadsheet tracked by hand.
export function computeMonthDistribution(
  vehicles: Record<string, any>[],
  dateField: "rego_date" | "cof_date",
  allowedMonths: number[],
  monthsAhead = 26,
): MonthBucket[] {
  const now = new Date();
  const startYear = now.getUTCFullYear();
  const startMonth = now.getUTCMonth();

  const buckets: MonthBucket[] = [];
  for (let i = 0; i < monthsAhead; i++) {
    const total = startMonth + i;
    const year = startYear + Math.floor(total / 12);
    const monthIndex0 = total % 12;
    buckets.push({
      year,
      monthIndex0,
      label: `${monthLabel(monthIndex0)}-${String(year).slice(2)}`,
      count: 0,
      allowed: allowedMonths.includes(monthIndex0 + 1),
    });
  }

  const bucketIndex = (year: number, monthIndex0: number) =>
    buckets.findIndex((b) => b.year === year && b.monthIndex0 === monthIndex0);

  for (const v of vehicles) {
    if (v.status && v.status !== "active") continue;
    // Contractor-owned vehicles: the company doesn't pay their Rego/COF, so
    // they don't belong in the spend-spacing count at all.
    if (v.is_contractor_vehicle) continue;
    const raw = v[dateField];
    if (!raw) continue;
    const d = parseDateOnly(String(raw));
    const idx = bucketIndex(d.getUTCFullYear(), d.getUTCMonth());
    if (idx >= 0) buckets[idx].count += 1;
  }

  return buckets;
}

export interface RenewalSuggestion {
  months: number;
  targetDate: string;
  targetLabel: string;
  targetCount: number;
}

function pickLightest(
  candidates: { months: number; date: Date }[],
  distribution: MonthBucket[],
): RenewalSuggestion | null {
  // On a tie, prefer whichever candidate comes SOONEST (candidates are
  // walked in ascending month order, so the first occurrence of the lowest
  // count already wins and later ties never override it) -- not whichever
  // is closest to a "normal" 12-month cycle, which previously produced odd
  // results like suggesting 13 months to reach October when 7 months to an
  // equally-empty April was sitting right there.
  let best: RenewalSuggestion | null = null;
  for (const c of candidates) {
    const bucket = distribution.find((b) => b.year === c.date.getUTCFullYear() && b.monthIndex0 === c.date.getUTCMonth());
    const count = bucket?.count ?? 0;
    const isBetter = !best || count < best.targetCount;
    if (isBetter) {
      best = {
        months: c.months,
        targetDate: c.date.toISOString().slice(0, 10),
        targetLabel: `${monthLabel(c.date.getUTCMonth())}-${String(c.date.getUTCFullYear()).slice(2)}`,
        targetCount: count,
      };
    }
  }
  return best;
}

// Suggests how many months to renew Rego for so the new expiry lands in
// whichever allowed month (within minMonths..maxMonths of the vehicle's
// current expiry) is currently the lightest -- greedily filling the
// emptiest bucket converges to the same even spread as the spreadsheet's
// "ideal per month" average, without needing to track every vehicle's
// full renewal history.
export function suggestRegoRenewal(
  currentRegoDate: string | null | undefined,
  vehicles: Record<string, any>[],
  allowedMonths: number[],
  excludeVehicleNumber?: string | null,
  minMonths = 3,
  maxMonths = 15,
): RenewalSuggestion | null {
  if (!allowedMonths.length) return null;
  const baseDate = currentRegoDate ? parseDateOnly(currentRegoDate) : new Date();
  const others = excludeVehicleNumber ? vehicles.filter((v) => v.vehicle_number !== excludeVehicleNumber) : vehicles;
  const distribution = computeMonthDistribution(others, "rego_date", allowedMonths, 36);

  const candidates: { months: number; date: Date }[] = [];
  for (let months = minMonths; months <= maxMonths; months++) {
    const date = addMonths(baseDate, months);
    if (allowedMonths.includes(date.getUTCMonth() + 1)) candidates.push({ months, date });
  }
  return pickLightest(candidates, distribution);
}

// Suggests a COF/WOF renewal date. The interval itself is fixed (this
// vehicle's own cof_interval_months, or the fleet default) -- this only
// looks at booking up to flexMonths EARLY (never late) to land in a
// lighter allowed month, per the file-header comment on flex direction.
export function suggestCofRenewal(
  currentCofDate: string | null | undefined,
  intervalMonths: number,
  vehicles: Record<string, any>[],
  allowedMonths: number[],
  flexMonths: number,
  excludeVehicleNumber?: string | null,
): RenewalSuggestion | null {
  if (!currentCofDate || !allowedMonths.length) return null;
  const baseDate = parseDateOnly(currentCofDate);
  const baseline = addMonths(baseDate, intervalMonths);
  const others = excludeVehicleNumber ? vehicles.filter((v) => v.vehicle_number !== excludeVehicleNumber) : vehicles;
  const distribution = computeMonthDistribution(others, "cof_date", allowedMonths, 36);

  const candidates: { months: number; date: Date }[] = [];
  for (let offset = -flexMonths; offset <= 0; offset++) {
    const date = addMonths(baseline, offset);
    if (allowedMonths.includes(date.getUTCMonth() + 1)) candidates.push({ months: intervalMonths + offset, date });
  }
  return pickLightest(candidates, distribution);
}
